<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import {
  ExtractionRequest,
  useImageExtractor,
} from '@/src/composables/useImageExtractor';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

const {
  extractAndZip,
  downloadZip,
  isProcessing,
  progress,
  status,
  generatedZipBlob,
} = useImageExtractor();
const currentStudyStore = useCurrentStudyStore();
const contourContainerStore = useContourContainerStore();

const maskType = ref<'binary' | 'signalIntensity'>('binary');
const imageFormat = ref<'png' | 'tiff'>('tiff');
const doBreatholdCropping = ref(true);

// Analysis Data Selection
const selectedStaticMasks = ref<string[]>([]);
const selectedOCGCurves = ref<string[]>([]);
const selectedPolarMaps = ref<string[]>([]);

const isStudyComplete = false;

const availableStaticMasks: string[] = [];

const availableOCGCurves: string[] = [];

const availablePolarMaps: string[] = [];

// Data structure for selection: { seriesUID: { sliceIndex: boolean } }
const selectedSlices = reactive<Record<string, Record<number, boolean>>>({});

interface AvailableSeriesSlice {
  images: Array<{ uid: string }>;
}

interface AvailableSeriesItem {
  uid: string;
  description: string;
  slices: AvailableSeriesSlice[];
}

type AvailableStudySeries = Array<{
  uid: string;
  slices: AvailableSeriesSlice[];
}>;

type SeriesInfoLookup = Record<
  string,
  {
    SeriesDescription?: string;
  }
>;

const availableSeries = computed<AvailableSeriesItem[]>(() => {
  const activeSeries = currentStudyStore.activeStudy?.series as
    | AvailableStudySeries
    | undefined;

  if (!activeSeries) return [];

  const seriesInfoDicom = currentStudyStore.seriesInfoDicom as SeriesInfoLookup;

  return activeSeries.map(series => ({
    uid: series.uid,
    description:
      seriesInfoDicom[series.uid]?.SeriesDescription || 'Unknown Series',
    slices: series.slices,
  }));
});

// Initialize selection state
watch(
  availableSeries,
  (newSeries: AvailableSeriesItem[]) => {
    newSeries.forEach((series) => {
      if (!selectedSlices[series.uid]) {
        selectedSlices[series.uid] = {};
      }
      series.slices.forEach((_, index) => {
        if (selectedSlices[series.uid][index] === undefined) {
          selectedSlices[series.uid][index] = false;
        }
      });
    });
  },
  { immediate: true, deep: true },
);

const isSeriesSelected = (seriesUID: string) => {
  const slices = selectedSlices[seriesUID];
  if (!slices) return false;
  return Object.values(slices).every(v => v);
};

const isSeriesIndeterminate = (seriesUID: string) => {
  const slices = selectedSlices[seriesUID];
  if (!slices) return false;
  const values = Object.values(slices);
  return values.some(v => v) && !values.every(v => v);
};

const toggleSeries = (seriesUID: string) => {
  const currentState = isSeriesSelected(seriesUID);
  const slices = selectedSlices[seriesUID];
  if (slices) {
    Object.keys(slices).forEach((key) => {
      slices[Number(key)] = !currentState;
    });
  }
};

const toggleSlice = (seriesUID: string, sliceIndex: number) => {
  selectedSlices[seriesUID][sliceIndex]
    = !selectedSlices[seriesUID][sliceIndex];
};

const hasSelection = computed(() => {
  return Object.values(selectedSlices).some(series =>
    Object.values(series).some(v => v),
  );
});

const handleExtract = async () => {
  const seriesGroups: ExtractionRequest['seriesGroups'] = [];
  const imageMapping: Record<string, Record<string, string[]>> = {};
  const imageUidIndexMapping: Record<
    string,
    Record<string, Record<string, number>>
  > = {};

  for (const series of availableSeries.value) {
    const seriesSelection = selectedSlices[series.uid];

    if (!seriesSelection) continue;

    const selectedImages: Array<{
      uid: string;
      sliceIndex: number;
      phaseIndex: number;
    }> = [];

    for (let sliceIdx = 0; sliceIdx < series.slices.length; sliceIdx++) {
      if (seriesSelection[sliceIdx]) {
        const { images } = series.slices[sliceIdx];
        let croppedImageUIDs = images;
        if (!imageUidIndexMapping[series.uid]) {
          imageUidIndexMapping[series.uid] = {};
        }
        if (!imageUidIndexMapping[series.uid][String(sliceIdx)]) {
          imageUidIndexMapping[series.uid][String(sliceIdx)] = {};
        }

        const firstImageUid = images[0]?.uid;
        const studyObj = useViewportActions().getStudyObjectByImageUID(
          firstImageUid || '',
        );
        const breathholdState = studyObj?.getBreathhold();
        if (!breathholdState) {
          croppedImageUIDs = images;
        }
        else {
          const start = breathholdState.getStart();
          const end = breathholdState.getEnd();
          if (doBreatholdCropping.value) {
            croppedImageUIDs = images.slice(start, end + 1);
          }
        }

        const contourFlags = croppedImageUIDs.map((img, phaseIndex) => {
          imageUidIndexMapping[series.uid][String(sliceIdx)][img.uid]
            = phaseIndex;
          return contourContainerStore.hasContours(img.uid);
        });

        const firstContourIndex = contourFlags.findIndex(
          hasContours => hasContours,
        );
        const lastContourIndex
          = firstContourIndex === -1
            ? -1
            : contourFlags.length
              - 1
              - [...contourFlags]
                .reverse()
                .findIndex(hasContours => hasContours);

        if (firstContourIndex === -1 || lastContourIndex === -1) {
          continue;
        }

        croppedImageUIDs.forEach((img, phaseIndex) => {
          if (phaseIndex < firstContourIndex || phaseIndex > lastContourIndex) {
            return;
          }

          selectedImages.push({
            uid: img.uid,
            sliceIndex: sliceIdx,
            phaseIndex,
          });
        });

        if (!imageMapping[series.uid]) {
          imageMapping[series.uid] = {};
        }
        imageMapping[series.uid][String(sliceIdx)] = croppedImageUIDs
          .slice(firstContourIndex, lastContourIndex + 1)
          .map(img => img.uid);
      }
    }

    if (selectedImages.length > 0) {
      seriesGroups.push({
        seriesUid: series.uid,
        seriesName: series.description,
        images: selectedImages,
      });
    }
  }

  if (seriesGroups.length === 0) {
    console.warn('No images selected for extraction.');
    return;
  }

  const request: ExtractionRequest = {
    seriesGroups,
    contoursToExtract: [],
    maskType: maskType.value,
    extractionMode: 'isolate',
    extractStaticMasks: selectedStaticMasks.value,
    extractOCGCurves: selectedOCGCurves.value,
    extractPolarMaps: selectedPolarMaps.value,
    imageMapping,
    imageUidIndexMapping,
    imageFormat: imageFormat.value,
  };

  await extractAndZip(request);
};
</script>

<template>
  <div class="image-clipboard-container simple-panel">
    <v-card class="pa-4" elevation="2" color="transparent" variant="flat">
      <v-card-title>Secret Folder Export</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <p>Select Series and Slices to export.</p>
          </v-col>

          <v-col cols="12" class="selection-container">
            <v-list density="compact">
              <v-list-group
                v-for="series in availableSeries"
                :key="series.uid"
                :value="series.uid"
              >
                <template #activator="{ props }">
                  <v-list-item v-bind="props">
                    <template #prepend>
                      <v-checkbox-btn
                        :model-value="isSeriesSelected(series.uid)"
                        :indeterminate="isSeriesIndeterminate(series.uid)"
                        @click.stop="toggleSeries(series.uid)"
                      />
                    </template>
                    <v-list-item-title>
                      {{
                        series.description
                      }}
                    </v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item
                  v-for="(slice, index) in series.slices"
                  :key="index"
                  :value="index"
                  @click.stop="toggleSlice(series.uid, index)"
                >
                  <template #prepend>
                    <v-checkbox-btn
                      v-if="selectedSlices[series.uid]"
                      v-model="selectedSlices[series.uid][index]"
                    />
                  </template>
                  <v-list-item-title>
                    Slice {{ index + 1 }} ({{
                      slice.images.length
                    }}
                    images)
                  </v-list-item-title>
                </v-list-item>
              </v-list-group>
            </v-list>
          </v-col>

          <v-col cols="12">
            <v-divider />
          </v-col>

          <!-- <v-col cols="12" md="6">
            <v-radio-group v-model="maskType" row label="Mask Type">
              <v-radio label="Binary" value="binary"></v-radio>
              <v-radio
                label="Signal Intensity"
                value="signalIntensity"
              ></v-radio>
            </v-radio-group>
          </v-col> -->

          <!-- V-select for doBreatholdCropping -->
          <v-col>
            <v-checkbox
              v-model="doBreatholdCropping"
              label="Crop to Breathhold Range"
            />
          </v-col>

          <!-- Analysis Data Selection -->
          <v-col v-if="isStudyComplete" cols="12">
            <v-divider class="mb-4" />
            <h3>Analysis Data</h3>

            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedStaticMasks"
                  :items="availableStaticMasks"
                  label="Static Masks"
                  multiple
                  chips
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedOCGCurves"
                  :items="availableOCGCurves"
                  label="OCG Curves"
                  multiple
                  chips
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedPolarMaps"
                  :items="availablePolarMaps"
                  label="Polar Maps"
                  multiple
                  chips
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12">
            <div v-if="isProcessing" class="d-flex align-center">
              <v-progress-circular
                indeterminate
                color="primary"
                class="mr-4"
              />
              <div>
                <div>{{ status }}</div>
                <v-progress-linear
                  :model-value="progress"
                  color="primary"
                  height="10"
                  striped
                  class="mt-2"
                  style="width: 300px"
                />
              </div>
            </div>
            <div
              v-else-if="generatedZipBlob"
              class="d-flex align-center justify-center mt-4"
            >
              <v-btn color="success" @click="downloadZip()">
                Download Zip
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="isProcessing || !hasSelection"
          @click="handleExtract"
        >
          Extract
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped>
.image-clipboard-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 10px;
}

.selection-container {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 4px;
  margin-bottom: 16px;
}
</style>
